import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { obtenerClienteDetalle, actualizarCliente } from '../servicios/api';

const EditarClientePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    useEffect(() => {
        const cargarCliente = async () => {
            try {
                const respuesta = await obtenerClienteDetalle(id);
                if (respuesta.exito) {
                    // Mapear nombres de la BD a nombres del formulario si es necesario
                    const cliente = {
                        ...respuesta.cliente,
                        nombre: respuesta.cliente.nombre_cliente || '',
                        email: respuesta.cliente.correo_electronico || ''
                    };
                    setFormData(cliente);
                } else {
                    setError(respuesta.mensaje);
                }
            } catch (err) {
                setError('No se pudo cargar la información del cliente.');
            } finally {
                setIsLoading(false);
            }
        };
        cargarCliente();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccessMessage(null);
        setIsSubmitting(true);

        try {
            const respuesta = await actualizarCliente(id, formData);
            if (respuesta.exito) {
                setSuccessMessage(respuesta.mensaje);
                setTimeout(() => navigate(`/clientes/${id}`), 2000); // Redirigir a la vista de detalles después de 2s
            } else {
                setError(respuesta.mensaje || 'Ocurrió un error al actualizar.');
            }
        } catch (err) {
            const errorMsg = err.response?.data?.mensaje || 'Error de conexión.';
            setError(errorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) return <div className="text-center p-10">Cargando cliente...</div>;
    if (error && !formData) return <div className="text-center p-10 text-red-500">Error: {error}</div>;

    return (
        <section className="bg-white p-5 max-w-4xl mx-auto shadow-lg rounded-lg">
            <div className="flex justify-between items-center mb-8 pb-4 border-b">
                <h2 className="text-3xl font-bold text-gray-800">Editar Cliente</h2>
                <Link to={`/clientes/${id}`} className="bg-gray-800 text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-gray-700">
                    &larr; Volver a Detalles
                </Link>
            </div>

            {formData && (
                <form onSubmit={handleSubmit} noValidate>
                    {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}
                    {successMessage && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">{successMessage}</div>}
                    
                    {/* Formulario similar a NuevoCliente, pero con valores del estado */}
                    <div className="mb-5">
                        <label htmlFor="nif" className="block mb-2 text-sm font-bold">NIF / NIE</label>
                        <input type="text" id="nif" name="nif" value={formData.nif || ''} onChange={handleChange} className="w-full p-3 border-2 border-gray-300 rounded-md" />
                    </div>
                    <div className="mb-5">
                        <label htmlFor="nombre" className="block mb-2 text-sm font-bold">Nombre completo *</label>
                        <input type="text" id="nombre" name="nombre" value={formData.nombre || ''} onChange={handleChange} required className="w-full p-3 border-2 border-gray-300 rounded-md" />
                    </div>
                    <div className="grid md:grid-cols-2 md:gap-6 mb-5">
                        <div>
                            <label htmlFor="email" className="block mb-2 text-sm font-bold">Email</label>
                            <input type="email" id="email" name="email" value={formData.email || ''} onChange={handleChange} className="w-full p-3 border-2 border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label htmlFor="telefono" className="block mb-2 text-sm font-bold">Teléfono</label>
                            <input type="tel" id="telefono" name="telefono" value={formData.telefono || ''} onChange={handleChange} className="w-full p-3 border-2 border-gray-300 rounded-md" />
                        </div>
                    </div>
                    <div className="mb-5">
                        <label htmlFor="direccion" className="block mb-2 text-sm font-bold">Dirección</label>
                        <input type="text" id="direccion" name="direccion" value={formData.direccion || ''} onChange={handleChange} className="w-full p-3 border-2 border-gray-300 rounded-md" />
                    </div>
                    <div className="grid md:grid-cols-2 md:gap-6 mb-5">
                        <div>
                            <label htmlFor="poblacion" className="block mb-2 text-sm font-bold">Población</label>
                            <input type="text" id="poblacion" name="poblacion" value={formData.poblacion || ''} onChange={handleChange} className="w-full p-3 border-2 border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label htmlFor="codigo_postal" className="block mb-2 text-sm font-bold">Código Postal</label>
                            <input type="text" id="codigo_postal" name="codigo_postal" value={formData.codigo_postal || ''} onChange={handleChange} className="w-full p-3 border-2 border-gray-300 rounded-md" />
                        </div>
                    </div>
                    <div className="mb-6">
                        <label htmlFor="notas" className="block mb-2 text-sm font-bold">Notas privadas</label>
                        <textarea id="notas" name="notas" rows="4" value={formData.notas || ''} onChange={handleChange} className="w-full p-3 border-2 border-gray-300 rounded-md"></textarea>
                    </div>

                    <div className="flex gap-4 justify-end pt-5 border-t">
                        <button type="submit" className="bg-[#ee6e3b] text-white px-8 py-3 rounded-md font-semibold hover:bg-[#d96233]" disabled={isSubmitting}>
                            {isSubmitting ? 'Guardando Cambios...' : 'Guardar Cambios'}
                        </button>
                    </div>
                </form>
            )}
        </section>
    );
};

export default EditarClientePage;
